import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  selectGuaranteesApplicationFileCapabilities,
  selectGuaranteesApplicationFileCapabilitiesError,
  selectGuaranteesApplicationFileCapabilitiesStatus,
  selectGuaranteesDeleteApplicationFileError,
  selectGuaranteesDeleteApplicationFileStatus,
  selectGuaranteesDownloadApplicationFileError,
  selectGuaranteesDownloadApplicationFileStatus,
  selectGuaranteesRegenerateApplicationFileError,
  selectGuaranteesRegenerateApplicationFileStatus,
  selectGuaranteesUploadApplicationFileError,
  selectGuaranteesUploadApplicationFileStatus,
} from '~/store/guarantees/selectors.ts';
import {
  deleteGuaranteesApplicationFile,
  downloadGuaranteesApplicationFile,
  fetchGuaranteesApplicationFileCapabilities,
  regenerateGuaranteesApplicationFile,
  uploadGuaranteesApplicationFile,
} from '~/store/guarantees/thunks/applicationFileThunks.ts';
import {
  GuaranteesFetchStatus,
  GuaranteesFileType,
  type TGuaranteesApplicationFileCapability,
} from '~/store/guarantees/types.ts';
import type {
  TDeleteGuaranteesApplicationFileActionParams,
  TDownloadGuaranteesApplicationFileActionParams,
  TGuaranteesApplicationFileActionState,
  TGuaranteesApplicationFileDownloadResult,
  TRegenerateGuaranteesApplicationFileActionParams,
  TUploadGuaranteesApplicationFileActionParams,
} from './types.ts';
import {
  findApplicationFileCapability,
  getApplicationFileReplacementFileId,
  isApplicationFileAlreadyExistsError,
  normalizeGuaranteesApplicationFileCapabilities,
} from './utils.ts';

export const useGuaranteesApplicationFilesCapabilities = (applicationId?: string | null) => {
  const dispatch = useAppDispatch();
  const applicationFileCapabilities = useSelector(selectGuaranteesApplicationFileCapabilities);
  const applicationFileCapabilitiesStatus = useSelector(
    selectGuaranteesApplicationFileCapabilitiesStatus
  );
  const applicationFileCapabilitiesError = useSelector(
    selectGuaranteesApplicationFileCapabilitiesError
  );
  const uploadStatus = useSelector(selectGuaranteesUploadApplicationFileStatus);
  const uploadError = useSelector(selectGuaranteesUploadApplicationFileError);
  const regenerateStatus = useSelector(selectGuaranteesRegenerateApplicationFileStatus);
  const regenerateError = useSelector(selectGuaranteesRegenerateApplicationFileError);
  const downloadStatus = useSelector(selectGuaranteesDownloadApplicationFileStatus);
  const downloadError = useSelector(selectGuaranteesDownloadApplicationFileError);
  const deleteStatus = useSelector(selectGuaranteesDeleteApplicationFileStatus);
  const deleteError = useSelector(selectGuaranteesDeleteApplicationFileError);
  const [activeAction, setActiveAction] =
    useState<TGuaranteesApplicationFileActionState>(null);
  const fileTypes = useMemo(
    () =>
      applicationId
        ? normalizeGuaranteesApplicationFileCapabilities({
            fileTypes: applicationFileCapabilities,
          })
        : [],
    [applicationFileCapabilities, applicationId]
  );
  const isLoading =
    !!applicationId && applicationFileCapabilitiesStatus === GuaranteesFetchStatus.Loading;
  const error = applicationId ? applicationFileCapabilitiesError : null;

  const refresh = useCallback(async (): Promise<TGuaranteesApplicationFileCapability[]> => {
    if (!applicationId) {
      return [];
    }

    const response = await dispatch(
      fetchGuaranteesApplicationFileCapabilities({ applicationId })
    ).unwrap();

    return normalizeGuaranteesApplicationFileCapabilities(response);
  }, [applicationId, dispatch]);

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  const upload = useCallback(
    async ({
      fileId,
      fileType,
      multipartFile,
    }: TUploadGuaranteesApplicationFileActionParams) => {
      if (!applicationId) {
        return null;
      }

      setActiveAction({ action: 'upload', fileId, fileType });

      try {
        let response = null;

        try {
          response = await dispatch(
            uploadGuaranteesApplicationFile({
              applicationId,
              fileId,
              fileType,
              multipartFile,
            })
          ).unwrap();
        } catch (requestError) {
          const replacementFileId = getApplicationFileReplacementFileId(
            findApplicationFileCapability(fileTypes, fileType)
          );

          if (
            fileType !== GuaranteesFileType.AM_OTHER_DOCUMENT &&
            !fileId &&
            replacementFileId &&
            isApplicationFileAlreadyExistsError(requestError)
          ) {
            response = await dispatch(
              uploadGuaranteesApplicationFile({
                applicationId,
                fileId: replacementFileId,
                fileType,
                multipartFile,
              })
            ).unwrap();
          } else {
            throw requestError;
          }
        }

        await refresh();
        return response;
      } finally {
        setActiveAction(null);
      }
    },
    [applicationId, dispatch, fileTypes, refresh]
  );

  const regenerate = useCallback(
    async ({ fileId, fileType }: TRegenerateGuaranteesApplicationFileActionParams) => {
      if (!applicationId) {
        return null;
      }

      setActiveAction({ action: 'regenerate', fileId, fileType });

      try {
        let response = null;

        try {
          response = await dispatch(
            regenerateGuaranteesApplicationFile({
              applicationId,
              fileId,
              fileType,
            })
          ).unwrap();
        } catch (requestError) {
          const replacementFileId = getApplicationFileReplacementFileId(
            findApplicationFileCapability(fileTypes, fileType)
          );

          if (!fileId && replacementFileId && isApplicationFileAlreadyExistsError(requestError)) {
            response = await dispatch(
              regenerateGuaranteesApplicationFile({
                applicationId,
                fileId: replacementFileId,
                fileType,
              })
            ).unwrap();
          } else {
            throw requestError;
          }
        }

        await refresh();
        return response;
      } finally {
        setActiveAction(null);
      }
    },
    [applicationId, dispatch, fileTypes, refresh]
  );

  const download = useCallback(
    async ({
      fileId,
      fileType,
      isAttached,
    }: TDownloadGuaranteesApplicationFileActionParams): Promise<TGuaranteesApplicationFileDownloadResult | null> => {
      if (!applicationId) {
        return null;
      }

      setActiveAction({ action: 'download', fileId, fileType });

      try {
        return await dispatch(
          downloadGuaranteesApplicationFile({
            applicationId,
            fileId,
            fileType,
            isAttached,
          })
        ).unwrap();
      } finally {
        setActiveAction(null);
      }
    },
    [applicationId, dispatch]
  );

  const remove = useCallback(
    async ({ fileId, fileType }: TDeleteGuaranteesApplicationFileActionParams): Promise<void> => {
      if (!applicationId) {
        return;
      }

      setActiveAction({ action: 'delete', fileId, fileType });

      try {
        await dispatch(deleteGuaranteesApplicationFile({ applicationId, fileId, fileType })).unwrap();
        await refresh();
      } finally {
        setActiveAction(null);
      }
    },
    [applicationId, dispatch, refresh]
  );

  return {
    activeAction,
    delete: remove,
    download,
    error,
    fileTypes,
    isLoading,
    deleteError,
    deleteStatus,
    downloadError,
    downloadStatus,
    refresh,
    regenerate,
    regenerateError,
    regenerateStatus,
    upload,
    uploadError,
    uploadStatus,
  };
};
